import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { judgeApi, getToken, setToken, clearToken, fileUrl, formatApiError } from "../lib/api";
import { ABILITIES } from "../lib/scoring";
import {
  Loader2, LogOut, User, Search, ArrowLeft, ChevronRight, Check, Save,
  Skull, SkipForward, ClipboardCheck, Clock3,
} from "lucide-react";

const emptyScores = () => ABILITIES.reduce((m, a) => ((m[a.key] = 0), m), {});
const GROUP_COLORS = [
  "#D91616", "#2563EB", "#16A34A", "#D97706", "#7C3AED", "#0891B2", "#DB2777", "#65A30D",
];
const groupColor = (g) => GROUP_COLORS[((g || 1) - 1) % GROUP_COLORS.length];

function GroupTab({ label, color, active, onClick, count, testid }) {
  const style = active && color ? { background: color, color: "#fff", borderColor: color } : {};
  return (
    <button onClick={onClick} data-testid={testid}
      className={`shrink-0 px-4 py-2.5 text-sm font-display uppercase tracking-tight border transition-colors ${active ? (color ? "" : "bg-blood text-white border-blood") : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"}`}
      style={style}>
      {label}
      {count && <span className={`ml-2 font-mono text-xs ${active ? "opacity-90" : "text-[#6B6862]"}`}>{count.scored}/{count.total}</span>}
    </button>
  );
}

export default function JudgeScoring() {
  const [params] = useSearchParams();
  const [booting, setBooting] = useState(true);
  const [judge, setJudge] = useState(null);

  const boot = useCallback(async () => {
    if (!getToken("judge")) { setBooting(false); return; }
    try {
      const { data } = await judgeApi.get("/judge/me");
      setJudge(data);
    } catch {
      clearToken("judge");
    } finally {
      setBooting(false);
    }
  }, []);

  useEffect(() => { boot(); }, [boot]);

  const onLogout = () => { clearToken("judge"); setJudge(null); };

  if (booting) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-blood" size={28} />
      </div>
    );
  }

  if (!judge) return <JudgeLogin prefill={params.get("code") || ""} onLogin={setJudge} />;
  return <ScoringApp judge={judge} onLogout={onLogout} />;
}

// ---------------------------------------------------------------------------
function JudgeLogin({ prefill, onLogin }) {
  const [code, setCode] = useState(prefill || "");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) { setErr("Please enter your name."); return; }
    if (code.trim().length < 4) { setErr("Enter your access code."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await judgeApi.post("/judge/login", { code: code.trim(), name: name.trim() });
      setToken(data.token, "judge");
      onLogin(data.judge);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (prefill && prefill.length >= 4) { /* auto-focus but no auto-submit */ } }, [prefill]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#F2EBE1] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <Skull className="mx-auto text-blood mb-4" size={44} />
        <h1 className="font-display text-3xl uppercase tracking-tight">Judge Scoring</h1>
        <p className="text-sm text-[#8a857d] mt-2 mb-8">Enter your name and the access code you were given for tonight.</p>
        <form onSubmit={submit} className="space-y-4">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="Your name"
            data-testid="judge-name-input"
            className="w-full text-center text-lg bg-[#0A0A0A] border border-[#262626] py-4 focus:outline-none focus:border-blood"
          />
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="6-digit code"
            data-testid="judge-code-input"
            className="w-full text-center tracking-[0.5em] font-mono text-2xl bg-[#0A0A0A] border border-[#262626] py-4 focus:outline-none focus:border-blood"
          />
          {err && <p className="text-sm text-blood" data-testid="judge-login-error">{err}</p>}
          <button type="submit" disabled={busy} data-testid="judge-login-btn"
            className="w-full flex items-center justify-center gap-2 bg-blood text-white py-4 text-sm uppercase tracking-wider hover:bg-[#FF3333] disabled:opacity-40">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Start Scoring
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ScoringApp({ judge, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [roles, setRoles] = useState([]);
  const [groupFilter, setGroupFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await judgeApi.get("/judge/applicants");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); judgeApi.get("/judge/roles").then(({ data }) => setRoles(data.items || [])).catch(() => {}); }, [load]);

  const groupsEnabled = !!judge.audition?.groups_enabled;
  const groupCount = groupsEnabled ? (judge.audition?.group_count || 0) : 0;
  const hasUngrouped = groupsEnabled && items.some((a) => !a.group);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = items;
    if (q) list = list.filter((a) => (a.full_name || "").toLowerCase().includes(q));
    if (groupFilter === "none") list = list.filter((a) => !a.group);
    else if (groupFilter !== "all") list = list.filter((a) => String(a.group) === String(groupFilter));
    return [...list].sort((a, b) => (a.full_name || "").localeCompare(b.full_name || ""));
  }, [items, search, groupFilter]);

  const groupCounts = useMemo(() => {
    const m = {};
    items.forEach((a) => { const k = a.group || "none"; if (!m[k]) m[k] = { total: 0, scored: 0 }; m[k].total++; if (a.score_status === "complete") m[k].scored++; });
    return m;
  }, [items]);

  const markScored = (id, status) => setItems((prev) => prev.map((a) => (a.id === id ? { ...a, score_status: status, scored: status !== "none" } : a)));

  const currentIndex = filtered.findIndex((a) => a.id === selectedId);
  const nextApplicant = () => {
    if (currentIndex < 0) return;
    const next = filtered[currentIndex + 1];
    setSelectedId(next ? next.id : null);
    if (!next) toast.success("That was the last applicant in the list.");
  };
  const nextUnscored = () => {
    const rest = filtered.slice(currentIndex + 1);
    const target = rest.find((a) => a.score_status !== "complete");
    if (target) setSelectedId(target.id);
    else { setSelectedId(null); toast.success("No more applicants left to finish below."); }
  };

  const scoredCount = items.filter((a) => a.score_status === "complete").length;

  if (selectedId) {
    return (
      <ScoreSheet
        key={selectedId}
        applicantId={selectedId}
        applicant={filtered.find((a) => a.id === selectedId)}
        roles={roles}
        position={{ index: currentIndex, total: filtered.length }}
        onBack={() => { setSelectedId(null); load(); }}
        onSaved={markScored}
        onNext={nextApplicant}
        onNextUnscored={nextUnscored}
        hasNext={currentIndex >= 0 && currentIndex < filtered.length - 1}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-[#F2EBE1] pb-10">
      <header className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#1a1a1a] px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-display text-lg uppercase tracking-tight truncate" data-testid="judge-name">{judge.name}</div>
          <div className="text-[11px] text-[#8a857d] truncate">{judge.audition?.name}</div>
        </div>
        <button onClick={onLogout} data-testid="judge-logout-btn"
          className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#8a857d] hover:text-blood shrink-0">
          <LogOut size={14} /> Exit
        </button>
      </header>

      <div className="px-4 pt-4">
        {groupsEnabled && groupCount > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-1 px-1" data-testid="judge-group-tabs">
            <GroupTab label="All" active={groupFilter === "all"} onClick={() => setGroupFilter("all")} testid="judge-group-all" />
            {Array.from({ length: groupCount }, (_, i) => i + 1).map((g) => (
              <GroupTab key={g} label={`Group ${g}`} color={groupColor(g)}
                active={String(groupFilter) === String(g)} onClick={() => setGroupFilter(g)}
                count={groupCounts[g]} testid={`judge-group-${g}`} />
            ))}
            {hasUngrouped && (
              <GroupTab label="Ungrouped" active={groupFilter === "none"} onClick={() => setGroupFilter("none")}
                count={groupCounts["none"]} testid="judge-group-none" />
            )}
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-[#8a857d] mb-3" data-testid="judge-progress">
          <ClipboardCheck size={14} className="text-blood" />
          You have scored <span className="text-[#F2EBE1] font-mono">{scoredCount}</span> of <span className="text-[#F2EBE1] font-mono">{items.length}</span>
          {groupFilter !== "all" && <span className="text-[#6B6862]">· showing {filtered.length}</span>}
        </div>
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6862]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name…" data-testid="judge-search"
            className="w-full bg-[#0A0A0A] border border-[#262626] pl-9 pr-3 py-3 text-sm focus:outline-none focus:border-blood" />
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-blood" size={24} /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-[#6B6862] py-12 text-center" data-testid="judge-empty">{groupFilter === "all" ? "No applicants to score." : "No applicants in this group."}</p>
        ) : (
          <div className="space-y-2" data-testid="judge-applicant-list">
            {filtered.map((a) => (
              <button key={a.id} onClick={() => setSelectedId(a.id)} data-testid={`judge-applicant-${a.id}`}
                className="w-full flex items-center gap-3 text-left border border-[#1a1a1a] bg-[#0A0A0A] p-3 hover:border-[#3a3835] transition-colors">
                <div className="w-12 h-12 shrink-0 border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
                  {a.headshot_file_id ? <img src={fileUrl(a.headshot_file_id, { thumb: true })} alt="" loading="lazy" className="w-full h-full object-cover" /> : <User size={20} className="text-[#3a3835]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase tracking-tight truncate">{a.full_name}</div>
                  {a.group && <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-bold" style={{ background: groupColor(a.group), color: "#fff" }}>Group {a.group}</span>}
                </div>
                {a.score_status === "complete" ? (
                  <span className="shrink-0 flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#22C55E]" data-testid={`judge-scored-${a.id}`}><Check size={14} /> Scored</span>
                ) : a.score_status === "partial" ? (
                  <span className="shrink-0 flex items-center gap-1 text-[11px] uppercase tracking-wider text-[#F59E0B]" data-testid={`judge-partial-${a.id}`}><Clock3 size={13} /> Partial</span>
                ) : (
                  <ChevronRight size={18} className="shrink-0 text-[#6B6862]" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ScoreSheet({ applicantId, applicant, roles, position, onBack, onSaved, onNext, onNextUnscored, hasNext }) {
  const [scores, setScores] = useState(emptyScores());
  const [touched, setTouched] = useState(() => new Set());
  const [notes, setNotes] = useState("");
  const [prefRoles, setPrefRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await judgeApi.get(`/judge/applicants/${applicantId}/score`);
        if (!alive) return;
        setPrefRoles(data.preferred_role_ids || []);
        const saved = data.score?.scores || {};
        const savedKeys = ABILITIES.filter((a) => typeof saved[a.key] === "number").map((a) => a.key);
        setScores({ ...emptyScores(), ...saved });
        setTouched(new Set(savedKeys));
        setNotes(data.score?.notes || "");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [applicantId]);

  const togglePref = (rid) => setPrefRoles((prev) => {
    if (prev.includes(rid)) return prev.filter((x) => x !== rid);
    if (prev.length >= 3) { toast.error("You can select up to 3 preferred roles."); return prev; }
    return [...prev, rid];
  });

  const scoredCount = touched.size;
  const status = scoredCount >= ABILITIES.length ? "complete" : (scoredCount > 0 ? "partial" : "none");

  const save = async ({ then } = {}) => {
    setSaving(true);
    try {
      const payload = {};
      ABILITIES.forEach((a) => { if (touched.has(a.key)) payload[a.key] = scores[a.key]; });
      await judgeApi.put(`/judge/applicants/${applicantId}/score`, { scores: payload, notes });
      await judgeApi.put(`/judge/applicants/${applicantId}/preferred-roles`, { role_ids: prefRoles });
      onSaved(applicantId, status);
      toast.success(status === "partial" ? "Partial score saved" : "Score saved");
      if (then === "next") onNext();
      else if (then === "list") onBack();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setVal = (key, v) => {
    setScores((s) => ({ ...s, [key]: v }));
    setTouched((t) => { const n = new Set(t); n.add(key); return n; });
  };
  const scoredVals = ABILITIES.filter((a) => touched.has(a.key)).map((a) => Number(scores[a.key]) || 0);
  const avg = scoredVals.length ? (scoredVals.reduce((s, v) => s + v, 0) / scoredVals.length).toFixed(1) : "–";

  return (
    <div className="min-h-screen bg-[#050505] text-[#F2EBE1] pb-40">
      <header className="sticky top-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-b border-[#1a1a1a] px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => onBack()} data-testid="judge-back-list-btn" className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#8a857d] hover:text-[#F2EBE1] shrink-0">
            <ArrowLeft size={16} /> List
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-2.5">
            <div className="w-9 h-9 shrink-0 rounded-full border border-[#262626] bg-[#050505] overflow-hidden flex items-center justify-center">
              {applicant?.headshot_file_id ? <img src={fileUrl(applicant.headshot_file_id, { thumb: true })} alt="" className="w-full h-full object-cover" /> : <User size={16} className="text-[#3a3835]" />}
            </div>
            <div className="min-w-0">
              <div className="font-display uppercase tracking-tight leading-tight truncate" data-testid="judge-scoring-name">{applicant?.full_name || "Applicant"}</div>
              <div className="text-[10px]" data-testid="judge-scoring-status">
                {position.total > 0 && <span className="text-[#6B6862]">{position.index + 1} of {position.total} · </span>}
                <span className={status === "complete" ? "text-[#22C55E]" : status === "partial" ? "text-[#F59E0B]" : "text-[#6B6862]"}>
                  {status === "complete" ? "all 12 scored" : `${scoredCount}/12 scored`}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-display text-2xl text-blood leading-none" data-testid="judge-live-avg">{avg}</div>
            <div className="text-[9px] uppercase tracking-wider text-[#6B6862]">avg</div>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-blood" size={24} /></div>
      ) : (
        <div className="px-4 pt-5 space-y-6" data-testid="judge-score-sheet">
          {ABILITIES.map((ab) => {
            const isSet = touched.has(ab.key);
            const v = Number(scores[ab.key]) || 0;
            return (
              <div key={ab.key} data-testid={`judge-ability-${ab.key}`}>
                <div className="flex items-baseline justify-between mb-1.5">
                  <div>
                    <span className="font-display uppercase tracking-tight text-sm">{ab.label}</span>
                    <span className="block text-[11px] text-[#6B6862]">{ab.hint}</span>
                  </div>
                  <button type="button" onClick={() => !isSet && setVal(ab.key, v)}
                    className="font-mono text-xl w-8 text-right" style={{ color: isSet ? "#F2EBE1" : "#4a4843" }} data-testid={`judge-val-${ab.key}`}>{isSet ? v : "–"}</button>
                </div>
                <input type="range" min="0" max="10" step="1" value={v}
                  onChange={(e) => setVal(ab.key, parseInt(e.target.value, 10))}
                  data-testid={`judge-slider-${ab.key}`}
                  className={`judge-range w-full ${isSet ? "" : "judge-range-unset"}`}
                  style={{ accentColor: "#D91616" }} />
                <div className="flex justify-between items-center text-[9px] text-[#4a4843] mt-0.5 font-mono px-0.5">
                  <span>0</span>
                  {!isSet && <span className="text-[#F59E0B] tracking-wider">NOT SCORED — SLIDE TO SET</span>}
                  <span>10</span>
                </div>
              </div>
            );
          })}

          <div>
            <label className="label-mini block mb-2">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="judge-notes"
              placeholder="Anything worth flagging about this performance…"
              className="w-full bg-[#0A0A0A] border border-[#262626] p-3 text-sm focus:outline-none focus:border-blood resize-y" />
          </div>

          <div data-testid="judge-preferred-roles">
            <label className="label-mini block mb-1">Preferred Roles <span className="text-[#4a4843]">· up to 3</span></label>
            <p className="text-[11px] text-[#6B6862] mb-2">Which characters would this applicant most like to play? ({prefRoles.length}/3 selected)</p>
            {roles.length === 0 ? (
              <p className="text-xs text-[#4a4843]">No roles have been created for this event yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {roles.map((r) => {
                  const sel = prefRoles.includes(r.id);
                  const idx = prefRoles.indexOf(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => togglePref(r.id)} data-testid={`judge-role-${r.id}`}
                      className={`px-3 py-2 text-sm border transition-colors ${sel ? "border-blood bg-blood/15 text-[#F2EBE1]" : "border-[#262626] text-[#A19D94] hover:border-[#3a3835]"}`}>
                      {sel && <span className="font-mono text-blood mr-1.5">{idx + 1}</span>}
                      {r.name}
                      {r.skill_level ? <span className="ml-1.5 text-[10px] text-[#6B6862]">· L{r.skill_level}</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-10 bg-[#0A0A0A]/95 backdrop-blur border-t border-[#1a1a1a] px-4 py-3 space-y-2">
        <button onClick={() => save({ then: hasNext ? "next" : "list" })} disabled={saving} data-testid="judge-save-next-btn"
          className="w-full flex items-center justify-center gap-2 bg-blood text-white py-3.5 text-sm uppercase tracking-wider hover:bg-[#FF3333] disabled:opacity-40">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {hasNext ? "Save & Next" : "Save & Finish"}
        </button>
        <div className="flex gap-2">
          <button onClick={() => save()} disabled={saving} data-testid="judge-save-btn"
            className="flex-1 flex items-center justify-center gap-1.5 border border-[#262626] py-2.5 text-xs uppercase tracking-wider text-[#A19D94] hover:text-[#F2EBE1] hover:border-[#3a3835] disabled:opacity-40">
            <Save size={13} /> Save Partial
          </button>
          <button onClick={onNextUnscored} disabled={saving} data-testid="judge-skip-btn"
            className="flex-1 flex items-center justify-center gap-1.5 border border-[#262626] py-2.5 text-xs uppercase tracking-wider text-[#A19D94] hover:text-[#F2EBE1] hover:border-[#3a3835] disabled:opacity-40">
            <SkipForward size={13} /> Skip
          </button>
        </div>
      </div>
    </div>
  );
}
